import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FlightProvider, useFlight } from './contexts/FlightContext';
import LoginScreen from './screens/LoginScreen';
import FlightSelectionScreen from './screens/FlightSelectionScreen';
import MainLayout from './components/MainLayout';
import { Tab, PwaIcon, NotificationStation } from './types';
import { CheckIcon, XCircleIcon, BellIcon, PaperAirplaneIcon, WifiSlashIcon, UsersIcon, BriefcaseIcon, IdentificationIcon, ShoppingCartIcon, ArchiveBoxIcon, TicketIcon, LockClosedIcon, TruckIcon, CheckBadgeIcon, ClipboardDocumentCheckIcon, XMarkIcon, ArrowSloganIcon, RestartIcon } from './components/Icons';
import { database } from './services/firebase';
import { ref, onValue } from 'firebase/database';
import InstallPwaPrompt from './components/InstallPwaPrompt';

const getStationIcon = (station: NotificationStation) => {
    switch (station) {
        case 'Crew Arrived':
        case '1st Pax IN':
        case 'Last Pax':
            return <UsersIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case '1st X-ray':
            return <BriefcaseIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Immigration In':
        case 'Immigration Out':
            return <IdentificationIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Duty Free':
            return <ShoppingCartIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'At Gate':
            return <ArchiveBoxIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'At Aircraft':
            return <PaperAirplaneIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Ready For Boarding':
            return <TicketIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Doors Closed':
            return <LockClosedIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Push Back':
            return <TruckIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'Acceptance Finalized':
            return <CheckBadgeIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        case 'System Clear':
        case 'Gate Clear':
            return <ClipboardDocumentCheckIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
        default:
            return <BellIcon className="w-8 h-8 mr-4 text-white flex-shrink-0" />;
    }
};

const UpdatePrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const onUpdate = (registration: ServiceWorkerRegistration) => {
      if(registration.waiting) {
        console.log('New service worker is waiting.', registration.waiting);
        waitingWorkerRef.current = registration.waiting;
        setShowPrompt(true);
      }
    };

    const trackInstalling = (worker: ServiceWorker) => {
        worker.addEventListener('statechange', () => {
            if (worker.state === 'installed') {
                navigator.serviceWorker.ready.then(reg => {
                    if (reg) onUpdate(reg);
                });
            }
        });
    };

    navigator.serviceWorker.ready.then(registration => {
      if (!registration) return;
      
      if (registration.waiting) { // A new worker is already waiting
        onUpdate(registration);
        return;
      }
      
      if (registration.installing) { // A new worker is installing
        trackInstalling(registration.installing);
        return;
      }

      // Listen for new workers that are found later
      registration.addEventListener('updatefound', () => {
        if(registration.installing) {
          trackInstalling(registration.installing);
        }
      });
    }).catch(error => {
        console.error('Error getting service worker registration:', error);
    });
    
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if(refreshing) return;
        refreshing = true;
        window.location.reload();
    });

  }, []);

  const reloadPage = () => {
    if (waitingWorkerRef.current) {
        waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
        setShowPrompt(false);
    }
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div role="alert" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] p-4 rounded-lg shadow-2xl text-white bg-gradient-to-r from-blue-600 to-cyan-600 border border-sky-400 max-w-md w-full animate-slide-up-fade-in no-print">
      <div className="flex items-center justify-between">
        <div className="flex-grow">
          <p className="font-bold text-lg">New Version Available</p>
          <p className="text-sm text-sky-200">Please refresh to get the latest updates.</p>
        </div>
        <button 
          onClick={reloadPage} 
          className="ml-4 flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-bold py-2 px-4 rounded-lg transition-colors"
        >
          <RestartIcon className="w-5 h-5" />
          Refresh
        </button>
      </div>
    </div>
  );
};


const App: React.FC = () => {
  return (
    <AuthProvider>
      <FlightProvider>
        <AppContent />
      </FlightProvider>
    </AuthProvider>
  );
};

const LoadingIndicator: React.FC = () => (
    <div className="min-h-screen flex flex-col items-center justify-center text-center bg-slate-900">
        <ArrowSloganIcon className="h-24 w-auto animate-pulse" style={{ animationDuration: '2s' }} />
        <h2 className="mt-6 text-2xl font-bold text-slate-200 animate-slide-up-fade-in">Preparing Dashboard...</h2>
        <p className="mt-2 text-slate-400 animate-slide-up-fade-in" style={{ animationDelay: '150ms' }}>Syncing real-time flight data.</p>
    </div>
);

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const { selectedFlightData, notification: localNotification, areNotificationsEnabled, isLoading: isFlightDataLoading } = useFlight();
  const [showTour, setShowTour] = useState(false);
  const [isTourInitialized, setIsTourInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  
  // State for global notifications from other users
  const [globalNotification, setGlobalNotification] = useState<{ station: NotificationStation; triggeredBy: string } | null>(null);
  const globalNotificationTimer = useRef<number | null>(null);
  const lastGlobalNotificationTimestamp = useRef<number | null>(null);

  useEffect(() => {
    const connectedRef = ref(database, '.info/connected');
    const unsubscribe = onValue(connectedRef, (snapshot) => {
        setIsOnline(snapshot.val() === true);
    });

    return () => {
        unsubscribe();
        if (globalNotificationTimer.current) {
            clearTimeout(globalNotificationTimer.current);
        }
    };
  }, []);

  // Effect to handle incoming remote notifications for visual toasts
  useEffect(() => {
    if (!selectedFlightData) return;
    const remoteNotification = selectedFlightData.lastNotification;
    if (!remoteNotification) return;

    // --- Visual Toast Logic for other users ---
    // Show a visual toast only if the notification is new and from another user
    if (remoteNotification.timestamp !== lastGlobalNotificationTimestamp.current) {
        lastGlobalNotificationTimestamp.current = remoteNotification.timestamp;
        
        if (remoteNotification.triggeredBy !== user?.username) {
            setGlobalNotification({
                station: remoteNotification.station,
                triggeredBy: remoteNotification.triggeredBy,
            });

            if (globalNotificationTimer.current) {
                clearTimeout(globalNotificationTimer.current);
            }
            globalNotificationTimer.current = window.setTimeout(() => {
                setGlobalNotification(null);
            }, 7000); // Show for 7 seconds
        }
    }
  }, [selectedFlightData, user?.username]);


  useEffect(() => {
    if (user && !isTourInitialized) {
      const hasCompletedTour = localStorage.getItem('hasCompletedTour');
      if (!hasCompletedTour) {
        setShowTour(true);
      }
      setIsTourInitialized(true);
    }
  }, [user, isTourInitialized]);

  const handleTourComplete = () => {
    localStorage.setItem('hasCompletedTour', 'true');
    setShowTour(false);
  };
  
  const handleDismissGlobalNotification = () => {
      if (globalNotificationTimer.current) {
          clearTimeout(globalNotificationTimer.current);
      }
      setGlobalNotification(null);
  };

  if (!user) {
    return <LoginScreen />;
  }

  if (isFlightDataLoading) {
      return <LoadingIndicator />;
  }
  
  const isFlightSessionActive = Object.keys(useFlight().activeFlights).length > 0;

  if (!isFlightSessionActive) {
    return <FlightSelectionScreen />;
  }

  const roleDefaultTabs: { [key: string]: Tab } = {
    ADMIN: Tab.Admin,
    SUPERVISOR: Tab.CounterDistribution,
    AGENT: Tab.CrewMovement,
    VIEWER: Tab.Overview,
  };
  
  const initialTab = roleDefaultTabs[user.role] || Tab.Overview;

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={`fixed top-0 left-0 right-0 bg-amber-600 text-white text-center text-sm py-1.5 z-[60] shadow-lg transition-transform duration-300 ease-in-out ${isOnline ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="container mx-auto flex items-center justify-center gap-2">
          <WifiSlashIcon className="w-5 h-5"/>
          <span>You are offline. Changes will sync when reconnected.</span>
        </div>
      </div>
      <MainLayout initialTab={initialTab} showTour={showTour} onTourComplete={handleTourComplete} />
      {/* Local notifications (for user's own actions) */}
      {localNotification && (
        <div role="alert" className={`toast-notification fixed top-5 right-5 z-50 p-4 rounded-lg shadow-lg text-white ${localNotification.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600 border border-green-400' : 'bg-gradient-to-r from-red-500 to-rose-600 border border-red-400'}`}>
          <div className="flex items-center">
            {localNotification.type === 'success' ? <CheckIcon className="w-6 h-6 mr-2"/> : <XCircleIcon className="w-6 h-6 mr-2"/>}
            {localNotification.message}
          </div>
        </div>
      )}
      {/* Global notifications (from other users) */}
      {globalNotification && areNotificationsEnabled && (
        <div role="alert" className="toast-notification fixed top-5 left-1/2 -translate-x-1/2 z-50 p-4 rounded-lg shadow-2xl text-white bg-gradient-to-r from-sky-600 to-cyan-600 border border-sky-400 max-w-md w-full">
           <div className="flex items-center">
            {getStationIcon(globalNotification.station)}
            <div className="flex-grow">
                <p className="font-bold text-lg">{globalNotification.station} recorded</p>
                <p className="text-sm text-sky-200">by {globalNotification.triggeredBy}</p>
            </div>
            <button onClick={handleDismissGlobalNotification} className="ml-4 p-1 rounded-full text-sky-200 hover:bg-sky-700/50 transition-colors flex-shrink-0" aria-label="Dismiss notification">
                <XMarkIcon className="w-5 h-5"/>
            </button>
           </div>
        </div>
      )}
      <InstallPwaPrompt />
      <UpdatePrompt />
    </>
  );
};

export default App;
